import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Pin, Lock, MessageSquare, Plus, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NewPostModal from "@/components/forums/NewPostModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POST_TYPES = [
  { key: "all", label: "All", icon: "🌐" },
  { key: "question", label: "Question", icon: "🤔" },
  { key: "win", label: "Win", icon: "💪" },
  { key: "vent", label: "Vent", icon: "😤" },
  { key: "recipe", label: "Recipe", icon: "🍳" },
];

const SORTS = [
  { key: "hot", label: "Hot", icon: <Flame size={12} /> },
  { key: "new", label: "New" },
  { key: "top", label: "Top" },
];

const TYPE_THEME = {
  question: { color: "#3B82F6", icon: "🤔" },
  win: { color: "#D4AF37", icon: "💪" },
  vent: { color: "#DC2626", icon: "😤" },
  recipe: { color: "#10B981", icon: "🍳" },
};

export default function Forums() {
  const { user } = useAuth();
  const [sort, setSort] = useState("hot");
  const [postType, setPostType] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/forums/feed?sort=${sort}&post_type=${postType}&limit=30`);
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, postType]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="forums-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">Community</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
              💬 Forums
            </h1>
            <p className="text-neutral-400 text-sm mt-2">
              Ask, win, vent, cook. Free users post 3/week · Premium unlimited.
            </p>
          </div>
          <button
            data-testid="forums-new-post-btn"
            onClick={() => {
              if (!user) { window.dispatchEvent(new Event("omni:open-auth")); return; }
              setModalOpen(true);
            }}
            className="btn-omni btn-red inline-flex items-center gap-2"
          >
            <Plus size={16} /> New Post
          </button>
        </div>

        {/* Type filter chips */}
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          {POST_TYPES.map((t) => (
            <button
              key={t.key}
              data-testid={`forums-type-${t.key}`}
              onClick={() => setPostType(t.key)}
              className={
                "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-2 transition-colors " +
                (postType === t.key
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "bg-transparent text-neutral-400 border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37]")
              }
            >
              <span>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Sort tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-900">
          {SORTS.map((s) => (
            <button
              key={s.key}
              data-testid={`forums-sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px transition-colors " +
                (sort === s.key
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-transparent text-neutral-500 hover:text-white")
              }
            >
              {s.icon}
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="forums-empty"
            className="text-center py-16 border-2 border-dashed border-zinc-800 bg-[#0F0F0F] text-neutral-500"
          >
            <div className="text-5xl mb-3">💬</div>
            <div className="font-display text-2xl text-white mb-2">No posts yet</div>
            <div className="text-sm">Be the first to start a conversation.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="forums-list">
            {items.map((p) => <PostRow key={p.post_id} post={p} />)}
          </ul>
        )}
      </main>
      <Footer />
      <NewPostModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => { setModalOpen(false); reload(); }}
      />
    </div>
  );
}

function PostRow({ post }) {
  const theme = TYPE_THEME[post.post_type] || { color: "#71717A", icon: "💬" };
  return (
    <li data-testid={`forums-post-${post.post_id}`}>
      <Link
        to={`/forums/p/${post.post_id}`}
        className="block bg-[#0F0F0F] border-2 border-zinc-900 hover:border-[#D4AF37] p-4 transition-colors"
        style={{ borderLeftColor: theme.color, borderLeftWidth: "3px" }}
      >
        <div className="flex items-start gap-4">
          <div className="text-3xl flex-shrink-0">{theme.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span
                className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-[1px]"
                style={{ backgroundColor: theme.color, color: "#0A0A0A" }}
              >
                {post.post_type}
              </span>
              {post.pinned && <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold"><Pin size={9}/> Pinned</span>}
              {post.locked && <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-neutral-500"><Lock size={9}/> Locked</span>}
              <AuthorLine author={post.author} />
              <span className="text-[10px] text-neutral-600">{timeAgo(post.created_at)}</span>
            </div>
            <div className="font-display text-xl text-white leading-tight">{post.title}</div>
            {post.preview && (
              <div className="text-sm text-neutral-400 mt-1 line-clamp-2">{post.preview}</div>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
              <span className="inline-flex items-center gap-1">
                <Flame size={12} className="text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-bold tabular-nums">{post.total_reactions}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <MessageSquare size={12} />
                <span className="tabular-nums">{post.comment_count}</span>
              </span>
              <ReactionBar reactions={post.reactions} />
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}

function AuthorLine({ author }) {
  if (!author) return null;
  const inner = (
    <span className="text-[10px] text-neutral-400 inline-flex items-center gap-1">
      {author.picture && <img src={author.picture} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
      <span className="font-bold text-neutral-300">{author.name}</span>
      {author.is_owner && <span className="text-[8px] uppercase tracking-widest text-[#D4AF37] font-bold">· Owner</span>}
    </span>
  );
  if (author.public && author.handle) {
    return <Link to={`/u/${author.handle}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#D4AF37]">{inner}</Link>;
  }
  return inner;
}

function ReactionBar({ reactions }) {
  const entries = Object.entries(reactions || {}).filter(([, n]) => n > 0);
  if (!entries.length) return null;
  return (
    <span className="inline-flex gap-1">
      {entries.map(([emoji, n]) => (
        <span key={emoji} className="inline-flex items-center gap-0.5 bg-zinc-900 px-1.5 py-0.5 text-[10px]">
          <span>{emoji}</span>
          <span className="tabular-nums text-neutral-400">{n}</span>
        </span>
      ))}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}
